import {
	createCrystallizeClient,
	type ProductGroupResult,
} from "@workspace/crystallize";
import type {
	Collection,
	ComponentChoiceComponentForLandingPageBlocks,
	Dish,
	LandingPage,
	Menu,
	Product,
	ProductVariant,
	ProductVariantForDish,
	ProductVariantForMenu,
	ProductVariantForService,
} from "@workspace/crystallize/generated/discovery/";
import { type DetailedError, parseResponse } from "hono/client";
import CollectionPage from "~/components/pages/collection-page";
import Page from "~/components/pages/page";
import ProductDetailPage from "~/components/pages/product-page";
import { type BeholdFeed, fetchBeholdFeed } from "~/utils/behold-feed.server";
import { filterNullValues } from "~/utils/helpers";
import {
	type DaySchedule,
	transformScheduleGrouped,
} from "~/utils/helpers/transform-opening-hours";
import { client } from "~/utils/rpc-client";
import type { Route } from "./+types/splat";

export interface DataForProductPage {
	title: string;
	shape: "menu" | "dish" | "default-product";
	selectedVariant: VariantUnion;
	product: ProductUnion;
	uniqueAllergens: Map<string, { id: string; name: string }>[];
	extras: ProductVariant[];
	services: ProductVariantForService[];
	seo: Menu["seo"] | Dish["seo"];
	isPurchaseable: boolean;
	availableFrom?: string;
}

type VariantUnion =
	| ProductVariantForDish
	| ProductVariantForMenu
	| ProductVariant;

type ProductUnion = Product | ProductVariantForDish | ProductVariantForMenu;

export interface DataForCollectionPage {
	shape: "collection";
	title: string;
	description: string;
	items: Product[];
	sections: ProductGroupResult["sections"];
	seo: Collection["seo"];
}

export interface DataForLandingPage {
	shape: "landing-page";
	title: string;
	blocks: ComponentChoiceComponentForLandingPageBlocks[];
	storeInfo?: {
		openingHours: DaySchedule[];
	};
	feed?: BeholdFeed;
	seo: LandingPage["seo"];
}

function nn<T>(xs: (T | null | undefined)[] | null | undefined): T[] {
	return (xs ?? []).filter((x): x is T => x != null);
}

export const loader = async ({
	params,
	request,
	context,
}: Route.LoaderArgs): Promise<
	DataForProductPage | DataForCollectionPage | DataForLandingPage
> => {
	const { "*": splat } = params;
	const { searchParams } = new URL(request.url);
	const queriedVariant = searchParams.get("variant");
	const previewMode = searchParams.get("preview") === "true";
	const {
		cloudflare: { env },
	} = context;

	const path = splat ? `/${splat?.split("/").filter(Boolean).join("/")}` : "/";

	try {
		const Crystallize = createCrystallizeClient({
			discovery: {
				tenantIdentifier: env.CRYSTALLIZE_TENANT_IDENTIFIER,
			},
		});

		const {
			search: { hits: [firstHit] = [] },
		} = (await Crystallize.discovery?.shape(path)) ?? { search: { hits: [] } };

		const { shape } = firstHit ?? { shape: null };

		if (!shape) {
			throw new Response("Page not found", { status: 404 });
		}

		switch (shape) {
			//TODO: Improve typing for product variants
			case "menu":
			case "default-product":
			case "dish": {
				const result = await (async () => {
					const { browse } = await Crystallize.discovery.product(path);
					if (!browse) throw new Response("Product not found", { status: 404 });

					const type = browse.menu.hits?.length
						? "menu"
						: browse.dish.hits?.length
							? "dish"
							: browse.defaultProduct.hits?.length
								? "defaultProduct"
								: null;

					const hits = type ? browse[type].hits : [];
					const [{ ...product }]: [Menu | Dish | Product] = hits;
					if (!product)
						throw new Response("Product not found", { status: 404 });
					if (!product.name)
						throw new Response("Product is missing a name or title", {
							status: 400,
						});

					const hasDishes = (v: VariantUnion): v is ProductVariantForMenu =>
						"dishes" in v;
					const hasServices = (v: VariantUnion): v is ProductVariantForMenu =>
						"services" in v;
					const hasExtrasOnVariant = (
						v: VariantUnion,
					): v is ProductVariantForMenu => "extras" in v;
					const hasExtrasOnProduct = (
						v: VariantUnion,
					): v is ProductVariantForDish =>
						"product" in v && v.product != null && "extras" in v.product;

					const variants: VariantUnion[] =
						product?.variants?.flatMap((v) => v ?? []) ?? [];

					const selectedVariant: VariantUnion =
						variants.find((v) => v.sku === queriedVariant) ??
						variants.find((v) => v.isDefault) ??
						(() => {
							throw new Error("Selected variant not found.");
						})();

					const dishes = hasDishes(selectedVariant)
						? nn(selectedVariant.dishes?.variants)
						: [];
					const extras = hasExtrasOnVariant(selectedVariant)
						? nn(selectedVariant.extras?.variants)
						: hasExtrasOnProduct(selectedVariant)
							? nn(selectedVariant.product?.extras?.variants)
							: [];
					const services = hasServices(selectedVariant)
						? nn(selectedVariant.services?.variants)
						: [];

					const uniqueExtras = Array.from(
						new Set(extras.map((e) => e?.sku).filter(Boolean)),
					)
						.map((sku) => extras.find((e) => e?.sku === sku))
						.filter((e): e is ProductVariant => e !== undefined);

					const uniqueAllergensMap = new Map<
						string,
						{ id?: string; name?: string; icon?: string }
					>();
					for (const d of dishes ?? []) {
						for (const allergen of d?.product?.foodProperties?.allergens
							?.allergens?.items ?? []) {
							uniqueAllergensMap.set(allergen?.id, {
								id: allergen?.id,
								name: allergen?.name,
								icon: allergen?.icon?.[0]?.url,
							});
						}
					}

					const allergens =
						selectedVariant.product?.foodProperties?.allergens?.allergens?.items.map(
							(allergen) => {
								return {
									id: allergen?.id,
									name: allergen?.name,
									icon: allergen?.icon?.[0]?.url,
								};
							},
						);

					const uniqueAllergens =
						uniqueAllergensMap.size > 0
							? Array.from(uniqueAllergensMap.values())
							: allergens;
					const isPurchaseable = (selectedVariant.defaultPrice ?? 0) > 0;

					return {
						title: product.title ?? product.name,
						shape,
						selectedVariant,
						product,
						uniqueAllergens,
						extras: uniqueExtras,
						...(services && { services }),
						seo: product.seo,
						isPurchaseable,
						...(product.availableFrom && {
							availableFrom: product.availableFrom,
						}),
					};
				})();

				return result;
			}
			case "collection": {
				return (async () => {
					const { title, description, items, sections, seo } =
						await Crystallize.discovery.collection(path);

					return {
						shape,
						title,
						description,
						items,
						sections,
						seo,
					};
				})();
			}
			case "landing-page": {
				return (async () => {
					const {
						browse: {
							landingPage: { hits },
						},
					} = await Crystallize.discovery.page(path, previewMode);

					const [{ blocks, seo, title, name }] = hits;

					const [rawOpeningHours, feed] = await Promise.all([
						parseResponse(client.distribution["opening-hours"].$get()).catch(
							(e: DetailedError) => {
								console.error("Error fetching opening hours:", e);
								throw new Response("Failed to fetch opening hours", {
									status: 500,
								});
							},
						),
						fetchBeholdFeed(env.kv).catch((e) => {
							console.error("Error fetching behold feed:", e);
							return undefined;
						}),
					]);

					const openingHours = transformScheduleGrouped(rawOpeningHours);

					const storeInfo = {
						openingHours,
					};

					return {
						title: title ?? name,
						seo,
						shape,
						blocks: filterNullValues(blocks),
						storeInfo,
						feed,
					};
				})();
			}
			default:
				throw new Response("Page not found", { status: 404 });
		}
	} catch (error) {
		if (error instanceof Response) {
			throw error;
		}
		console.error("Error fetching splat route data:", error);
		throw new Response("Failed to load splat route data", { status: 500 });
	}
};

export const meta = ({ data }: Route.MetaArgs) => {
	if (data?.seo) {
		return [
			{
				title: data.seo.title,
			},
			{
				name: "description",
				content: data.seo.description,
			},
		];
	}

	return [
		{
			title: data?.title,
			//TODO: Add description
		},
	];
};

export default function StorefrontSplatRoute({
	loaderData,
}: Route.ComponentProps) {
	switch (loaderData.shape) {
		case "menu":
		case "dish":
		case "default-product":
			return <ProductDetailPage data={loaderData} />;
		case "collection":
			return <CollectionPage data={loaderData} />;
		case "landing-page":
			return <Page data={loaderData} />;
		default:
			return <div>Page not found in frontend</div>;
	}
}
